/**
 * @module services/supabaseClient
 * @description Supabase client for Refero proxy mode
 *
 * When proxy mode is enabled, this client handles:
 * - Authentication with Spectral Cloud Portal
 * - Proxying Zotero API requests through Edge Functions
 *
 * The API key is stored encrypted in Supabase and never exposed to the client.
 */

import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js';
import { api } from '@/config';
import { createLogger } from './loggerService';

const log = createLogger('supabase');

// =============================================================================
// CLIENT INITIALIZATION
// =============================================================================

let supabaseClient: SupabaseClient | null = null;

/**
 * Get or create Supabase client
 */
export function getSupabaseClient(): SupabaseClient | null {
  if (!api.proxy.enabled) {
    return null;
  }

  if (!api.proxy.supabaseUrl || !api.proxy.supabaseAnonKey) {
    log.warn('Supabase credentials not configured for proxy mode');
    return null;
  }

  if (!supabaseClient) {
    supabaseClient = createClient(api.proxy.supabaseUrl, api.proxy.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
    log.info('Supabase client initialized');
  }

  return supabaseClient;
}

/**
 * Handle SSO tokens from URL hash (from Portal redirect)
 * Called on app initialization to check for and process SSO tokens
 */
export async function handleSsoFromUrl(): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;

  // Check for SSO tokens in URL hash
  const hash = window.location.hash.substring(1);
  if (!hash) return false;

  const params = new URLSearchParams(hash);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');

  // Only process if it looks like SSO tokens from Portal
  if (type !== 'sso' || !accessToken || !refreshToken) {
    return false;
  }

  log.info('SSO tokens detected in URL, setting session');

  try {
    const { data, error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      log.error('Failed to set session from SSO tokens', { error: error.message });
      return false;
    }

    // Clear the URL hash after successful session set
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    log.info('SSO session set successfully', { userId: data.user?.id });
    return true;
  } catch (error) {
    log.error('Error handling SSO tokens', { error });
    return false;
  }
}

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

/**
 * Get current session
 */
export async function getSession(): Promise<Session | null> {
  const client = getSupabaseClient();
  if (!client) return null;

  const { data } = await client.auth.getSession();
  return data.session;
}

/**
 * Get access token for Edge Function calls
 */
export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.access_token || null;
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getSession();
  return !!session;
}

/**
 * Sign out from Supabase
 */
export async function signOut(): Promise<void> {
  const client = getSupabaseClient();
  if (client) {
    await client.auth.signOut();
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  const client = getSupabaseClient();
  if (!client) {
    return { success: false, error: 'Supabase client not available' };
  }

  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    log.error('Sign in failed', { error: error.message });
    return { success: false, error: error.message };
  }

  log.info('Sign in successful', { userId: data.user?.id });
  return { success: true };
}

// =============================================================================
// ZOTERO PROXY
// =============================================================================

interface ProxyRequestOptions {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  /** Custom headers to pass to Zotero API (e.g., If-Unmodified-Since-Version) */
  zoteroHeaders?: Record<string, string>;
  /** Expected response type - 'json' (default) or 'text' for export formats */
  responseType?: 'json' | 'text';
  /** AbortSignal for cancelling requests */
  signal?: AbortSignal;
}

interface ProxyResponse<T> {
  data: T;
  headers: {
    totalResults?: number;
    lastModifiedVersion?: number;
    link?: string;
  };
}

/**
 * Make a proxied request to Zotero API via Edge Function
 */
export async function proxyZoteroRequest<T>(
  options: ProxyRequestOptions
): Promise<ProxyResponse<T>> {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase client not available');
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const { path, method = 'GET', body, params, zoteroHeaders, signal } = options;

  // Build the proxy URL
  const proxyUrl = `${api.proxy.supabaseUrl}/functions/v1/${api.proxy.zoteroProxyFunction}`;

  // Build query params for GET requests
  let url = proxyUrl;
  if (method === 'GET' && params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url = `${proxyUrl}?${queryString}`;
    }
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  // For GET requests, pass the Zotero path via header
  if (method === 'GET') {
    headers['x-zotero-path'] = path;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    signal,
  };

  // For non-GET requests, include path, headers, and body in request body
  if (method !== 'GET') {
    fetchOptions.body = JSON.stringify({
      path,
      method,
      body,
      params,
      headers: zoteroHeaders,
    });
  }

  log.debug('Proxy request', { path, method });

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle specific error codes
    if (errorData.code === 'NO_API_KEY') {
      throw new Error(
        'Zotero API key not configured. Please add it in Spectral Cloud Portal settings.'
      );
    }

    throw new Error(errorData.error || `Proxy request failed: ${response.status}`);
  }

  // Handle empty responses (e.g., 204 No Content) gracefully
  const text = await response.text();
  let data: T;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      log.warn('Failed to parse response as JSON', { text: text.substring(0, 100) });
      data = text as unknown as T;
    }
  } else {
    data = null as unknown as T;
  }

  return {
    data,
    headers: {
      totalResults: parseInt(response.headers.get('Total-Results') || '0', 10),
      lastModifiedVersion: parseInt(response.headers.get('Last-Modified-Version') || '0', 10),
      link: response.headers.get('Link') || undefined,
    },
  };
}

/**
 * Check if Zotero API key is configured in Spectral Cloud
 */
export async function isZoteroConfigured(): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;

  const accessToken = await getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(`${api.proxy.supabaseUrl}/functions/v1/api-keys`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return false;

    const { keys } = await response.json();
    return keys?.some((k: { service: string }) => k.service === 'zotero') || false;
  } catch {
    return false;
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const supabase = {
  getClient: getSupabaseClient,
  getSession,
  getAccessToken,
  isAuthenticated,
  signInWithEmail,
  signOut,
  proxyZoteroRequest,
  isZoteroConfigured,
};

export default supabase;
